import React, { useMemo, useRef, useState } from 'react';
import styles from './CompanySelection.module.scss';
import { useTranslation } from 'react-i18next';
import useOutsideClick from '../../hooks/useOutsideClick';
import SearchComponent from '../SearchComponent/SearchComponent';
import { CustomButton } from '../Button/Button';
import Icon from '../Icon/Icon';
import cx from 'classnames';

const CompanySelection = ({ onSelect, currentCompany = null, companies }) => {
  const [collapse, setCollapsed] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const fieldRef = useRef();

  const { t } = useTranslation();

  const companiesToShow = useMemo(() => {
    if (searchValue.length > 0) {
      return companies.filter(item => item.name.toLowerCase().includes(searchValue.toLowerCase()));
    }
    if (!collapse) {
      return null;
    }
    return companies;
  }, [companies, searchValue, collapse]);

  useOutsideClick(fieldRef, () => {
    if ((collapse || searchValue.length) && !searchActive) {
      setCollapsed(false);
    }
  });

  const handleSearchActive = () => {
    setCollapsed(true);
    setSearchActive(true);
  };

  const toggleCollapse = () => {
    setCollapsed(prevState => {
      if (prevState) {
        setSearchActive(false);
      }
      return !prevState;
    });
  };

  const handleSelect = item => {
    onSelect(item);
    setCollapsed(false);
    setSearchValue('');
    setSearchActive(false);
  };

  return (
    <div className={styles.searchComponentWrapper}>
      <SearchComponent
        onChange={setSearchValue}
        searchValue={searchValue}
        alwaysShow
        onFocus={handleSearchActive}
        onBlur={() => setSearchActive(false)}
        placeholder={currentCompany?.name}
        inputClassName={cx({ [styles.companyInputStyles]: currentCompany !== null })}
      />
      <CustomButton className={cx(styles.arrowButton)} variant="link" onClick={toggleCollapse} ariaLabel="collapse">
        <Icon src="arrowDown" className={cx(styles.arrowIcon, { [styles.collapsed]: collapse })} />
      </CustomButton>
      {companiesToShow ? (
        <ul className={cx(styles.companiesList, { [styles.active]: collapse || searchValue.length })} ref={fieldRef}>
          {companiesToShow.length ? (
            companiesToShow.map(item => (
              <li
                key={item.id}
                className={cx(styles.companyItem, {
                  [styles.withCurrent]: currentCompany !== null,
                  [styles.active]: item.id === currentCompany?.id,
                })}
                onClick={() => handleSelect(item)}
              >
                {item.name}
              </li>
            ))
          ) : (
            <li>{`${t('dashboard.searching.no_matches')} '${searchValue}'`}</li>
          )}
        </ul>
      ) : null}
    </div>
  );
};

export default CompanySelection;
